/*global SunergoPlatform*/

/**
 * Initialize Conditional Field handling.
 * @param {object} form - the form item.
 * @returns nothing if there are no conditional fields in form.
 */
const initConditionalFields = function(form) {
	const inputEvents = SunergoPlatform.inputEvents;
	const inputElements = SunergoPlatform.inputElements;

	// Find all dependent fields
	const foundDependents = form.element.querySelectorAll(
		'[data-dependent-name][data-dependent-value]'
	);
	if (!foundDependents.length) return;

	form.conditionalFields = {};
	foundDependents.forEach(function(field) {
		// Get the parents that are input or select fields.
		// Multiple parents are possible in fieldsets such as radio options.
		const parentName = field.dataset.dependentName;
		const parents = form.element.querySelectorAll(
			`input[name="${parentName}"], select[name="${parentName}"]`
		);

		// Check if the parent field is found.
		if (!parents || parents.length < 1)
			return console.warn(`[Form / Conditional Field] Missing parent: ${parentName}`, field);

		// If parent isn't already added to the dependent field list, add it in.
		if (!(parentName in form.conditionalFields)) {
			const item = {
				parents: parents,
				fieldType: parents[0].dataset.fieldType || parents[0].type,
				parentAttributes: {},
				discreteParents: false
			};

			/**
			 * Initializes the item.
			 */
			item.init = function() {
				// For checkbox, options are independent of each other.
				this.discreteParents = this.fieldType == 'checkbox';

				const item = this;
				this.parents.forEach(function(parent) {
					// Check initial state of values and set visibility accordingly.
					item.checkParentValue(parent);

					// Listen for parent clicks and set visibility accordingly.
					for (let event in inputEvents) {
						parent.addEventListener(inputEvents[event], function() {
							item.checkParentValue(parent);
						});
					}
				});
			};

			/**
			 * Enable dependent children.
			 * @param {HTMLElement} child - the child field.
			 * @param {boolean} visible - true to enable children.
			 */
			item.enableChildren = function(child, visible) {

				// Set the child field's visibility and disabled state.
				child.dataset.dependentHidden = !visible;

				// If this is a fieldset, disable it.
				if (child.dataset.fieldsetId) child.disabled = !visible;

				// Find and disable the actual field child.
				// If no child field, this is probably a labelOnly type.
				const childInput = child.querySelector(`${inputElements}, fieldset`);
				if (!childInput) return;

				childInput.disabled = !visible;

				// JS-controlled fields require some extra attention.
				// Tom Select needs to be disabled also.
				if (childInput.dataset.tomSelect) {
					if (visible) childInput.tomselect.enable();
					else childInput.tomselect.disable();
				}
			};

			/**
			 * Check the parent value's dependent value.
			 * @param {object} parent - the parent field.
			 */
			item.checkParentValue = function(parent) {
				// By default, set visibility based on input being checked.
				let element = parent;
				let visible = element.checked;

				switch (this.fieldType) {
					// For select, use the specific selected option.
					case 'select':
						if (parent.options && parent.options.length > 0)
							element = parent.selectedOptions[0];
						visible = true;
						break;

					// For checkboxSingle, children will always be visible when checked.
					case 'checkboxSingle':
						visible = true;
						break;

					case 'radio':
					case 'checkbox':
						// For radio and checkbox, use the checked value.
						visible = element.checked;
						break;

					default:
						visible = true;
						break;
				}

				Object.entries(this.parentAttributes).forEach(([attribute, possibleValues]) => {
					// Find the value of the attribute.
					let attributeValue = element.getAttribute(attribute);

					switch (this.fieldType) {
						case 'checkboxSingle':
							attributeValue = String(element.checked);
							break;
						case 'radio':
						case 'select':
						case 'checkbox':
							break;
						default:
							// For text inputs, check if the value is empty.
							attributeValue = String(element.value !== '');
							break;
					}

					// Get all children that use this attribute.
					const matchedChildren = possibleValues[attributeValue];

					if (matchedChildren) {
						// Enable matched children.
						matchedChildren.forEach(child => this.enableChildren(child, visible));
					}

					// Disable unmatched children if parents are connected.
					if (visible && !this.discreteParents) {
						Object.entries(possibleValues).forEach(([key, children]) => {
							// Skip the matched value, since we don't want to disable that child.
							if (key == attributeValue) return;

							// Disable children in the possible values.
							children.forEach(child => this.enableChildren(child, false));
						});
					}
				});
			};

			// Add the item with the parent name as the ID.
			form.conditionalFields[parentName] = item;
		}

		/*** Setup this field ***/
		const conditionalGroup = form.conditionalFields[parentName];
		const parentAttributes = conditionalGroup.parentAttributes;

		// Set the attribute based on the dependent attribute (if available),
		// "checked" value (if the type is a checkbox), or the value (by default).
		const attribute = field.dataset.dependentValueKey
			? field.dataset.dependentValueKey
			: conditionalGroup.fieldType == 'checkboxSingle'
				? 'checked'
				: 'value';

		// If the attribute isn't in the list yet, add it in.
		if (!(attribute in parentAttributes)) parentAttributes[attribute] = {};
		const valuesObject = parentAttributes[attribute];

		// Add the field to the value list.
		// Multiple "identical" fields, such as radio options, may be added.
		const dependentValue = field.dataset.dependentValue;
		if (!(dependentValue in valuesObject)) valuesObject[dependentValue] = [field];
		else valuesObject[dependentValue].push(field);
	});

	// Initialize everything.
	for (const group in form.conditionalFields) {
		form.conditionalFields[group].init();
	}
};

export default initConditionalFields;